import { selectPlaygroundFeatures, selectPlaygroundRefreshPosition } from './playgroundSlice'
import useElementDimensions from 'hooks/useElementDimensions'
import useStageMove from './hooks/useStageMove'
import { useReduxSelector } from 'store'
import React from 'react'

const Stage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const features = useReduxSelector(selectPlaygroundFeatures)
  const refreshPosition = useReduxSelector(selectPlaygroundRefreshPosition)
  const { width = 1, height = 1, rotation = 0, isGrouped, stageScale } = features || {}

  const stageRef = React.useRef<HTMLDivElement>(null)
  const stageMainRef = React.useRef<HTMLDivElement>(null)
  const stagePositionRef = React.useRef<HTMLDivElement>(null)
  const stageContainerRef = React.useRef<HTMLDivElement>(null)

  const [scale, setScale] = React.useState(stageScale || 0.5)
  const [position, setPosition] = React.useState(getCenterPositionInElement(stageRef))

  const { handleMouse, handleTouch, onWheel } = useStageMove(scale, position, stageRef, stagePositionRef, stageContainerRef, stageMainRef, setScale, setPosition)


  useElementDimensions(stageRef, () => {
    setScale(getInitialScale(stageRef, width, height))
    setPosition(getCenterPositionInElement(stageRef))
  })

  React.useEffect(() => {
    setScale(getInitialScale(stageRef, width, height))
    setPosition(getCenterPositionInElement(stageRef))
  }, [width, height, refreshPosition])

  React.useEffect(() => {
    if (isGrouped) {
      let previousPosition: { x: number, y: number }
      setPosition(p => {
        previousPosition = p
        return getCenterPositionInElement(stageRef)
      })

      return () => {
        if (previousPosition) setPosition(previousPosition)
      }
    }
  }, [isGrouped])


  return (
    <div
      ref={stageRef}
      className='playground__stage'
      onMouseDown={handleMouse}
      onTouchStart={handleTouch}
      onWheel={onWheel}>

      <div
        ref={stagePositionRef}
        className='stage__position'
        style={{
          top: `${position.y}px`,
          left: `${position.x}px`
        }}>

        <div
          ref={stageContainerRef}
          className='stage__conteiner'
          style={{
            width: `${scale * width}px`,
            height: `${scale * height}px`,
          }}>

          <div
            ref={stageMainRef}
            className={`stage__main${isGrouped ? ' --grouped' : ''}`}
            style={{
              '--stage-scale': `${scale}`,
              rotate: `${rotation}deg`,
              width: `${width}px`,
              height: `${height}px`,
              scale: `${scale}`
            } as React.CSSProperties}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}


const getInitialScale = (ref: React.RefObject<HTMLDivElement>, width: number, height: number) => {
  const maxWidth = ref.current?.clientWidth || window.innerWidth
  const maxHeight = ref.current?.clientHeight || window.innerHeight
  return Math.min(((maxWidth - 50) / width), ((maxHeight - 50) / height))
}

const getCenterPositionInElement = (ref: React.RefObject<HTMLDivElement>) => {
  const width = ref.current?.clientWidth || window.innerWidth
  const height = ref.current?.clientHeight || window.innerHeight
  return { x: width / 2.0, y: height / 2.0 }
}

export default Stage